import React from 'react';
import { useForm, useFormState } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import TimeStamp from '../common/TimeStamp';
import Row from '../common/Row';
import Select from '../common/Select';
import Errors from '../common/Errors';
import {
  widthOptions,
  validatorOptions,
  checkCharLimit,
  checkCharMin,
  noZeros,
} from './constants/editField';
import * as f from './constants/subtypes';
import { handleUpdateField } from './constants/handleField';
import Icon from '../common/Icon';

const FieldEdit = (props) => {
  const { field, toggleEdit, informant, setField } = props;
  const { created_at, type, updated_at, id, ...editableFields } = field;
  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    getValues,
    setError,
    formState: { errors },
  } = useForm({
    defaultValues: { ...editableFields },
    criteriaMode: 'all',
  });
  const { isDirty, dirtyFields } = useFormState({ control });
  const globalInformantExists = informant;
  const isInformant = informant == field.id;

  const onSubmit = async (formData) => {
    if (isDirty) {
      let updated = await handleUpdateField(id, formData);
      if (updated.ok) {
        setField(updated.data);
        toggleEdit();
      } else if (updated.errors) {
        Object.entries(updated.errors).forEach(([key, message]) =>
          setError(key, { types: { [key]: message } })
        );
      }
    } else {
      toggleEdit();
    }
  };

  function makeGlobalInformerRequired() {
    if (watch('informs_global') === true) {
      setValue('required', true, { shouldDirty: true });
    }
  }

  function watchQuickEdit() {
    if (watch('quick_edit') === false) {
      setValue('universal_quick_edit', false, { shouldDirty: true });
      setValue('unread_quick_edit', false, { shouldDirty: true });
    }
  }

  return (
    <>
      <div className='details'>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className={'controls lower'}>
            <button type='submit'>
              Save
              <Icon icon='save' />
            </button>
            <button
              className='cancel blue'
              onClick={(e) => {
                e.preventDefault();
                toggleEdit();
              }}
            >
              Cancel
              <Icon icon='xmark' />
            </button>
          </div>
          <Row name='Display Name:'>
            <input {...register('display_name')} />
          </Row>
          <Row name='Name:'>
            <input {...register('name', { required: 'Required' })} />
            <ErrorMessage
              errors={errors}
              name='name'
              render={({ messages }) => <Errors messages={messages} />}
            />
          </Row>
          <Row name='Help Text:'>
            <input {...register('help_text')} />
          </Row>
          <Row name='Type:' value={type} />
          {f.allowQuickEdit(type) && (
            <>
              <Row name='Quick Edit Allowed?'>
                <input
                  type='checkbox'
                  id='field-quick-edit'
                  {...register('quick_edit', {
                    onChange: () => watchQuickEdit(),
                  })}
                />
                <label htmlFor='field-quick-edit'></label>
              </Row>
              {watch('quick_edit') == true && (
                <>
                  <Row name='Allow Quick Edit for All Roles?'>
                    <input
                      type='checkbox'
                      id='field-universal-quick-edit'
                      {...register('universal_quick_edit')}
                    />
                    <label htmlFor='field-universal-quick-edit'></label>
                  </Row>
                  <Row name='Allow Quick Edit for Unread Submissions?'>
                    <input
                      type='checkbox'
                      id='field-unread-quick-edit'
                      {...register('unread_quick_edit')}
                    />
                    <label htmlFor='field-unread-quick-edit'></label>
                  </Row>
                </>
              )}
            </>
          )}
          {f.isAllowedQuota(type) && (
            <Row name='Informs Global:'>
              {globalInformantExists && !isInformant ? (
                'Unavailable'
              ) : (
                <>
                  <input
                    type='checkbox'
                    id='field-global-informer'
                    {...register('informs_global', {
                      onChange: () => makeGlobalInformerRequired(),
                    })}
                  />
                  <label htmlFor='field-global-informer'></label>
                </>
              )}
            </Row>
          )}
          <Row name='Required:'>
            <input
              type='checkbox'
              id='field-required'
              {...register('required')}
              disabled={watch('informs_global') == true}
            />
            <label htmlFor='field-required'></label>
          </Row>
          {f.canChangeWidth(type) && (
            <Row name='Width:'>
              <Select
                content={widthOptions}
                register={{ ...register('width') }}
              />
            </Row>
          )}
          {f.isFileType(type) && (
            <Row name='Allowed Files:'>
              <input {...register('file_ext_whitelist')} />
              <a
                href='https://developer.mozilla.org/en-US/docs/Web/HTTP/Basics_of_HTTP/MIME_types/Common_types'
                target='_blank'
              >
                View Common MIME Types
              </a>
            </Row>
          )}
          {f.isOptionType(type) && (
            <Row name='Option Validator:'>
              <Select
                content={validatorOptions.filter((v) =>
                  ['none', 'date'].includes(v.label)
                )}
                register={{ ...register('validator') }}
              />
            </Row>
          )}
          {f.isTextType(type) && (
            <>
              <Row name='Validator:'>
                <Select
                  content={validatorOptions}
                  register={{ ...register('validator') }}
                />
              </Row>
              {f.validatorLimits(type) && (
                <>
                  <Row name='Character minimum:'>
                    <input
                      type='number'
                      min={0}
                      {...register('char_min', {
                        validate: {
                          min: (v) => checkCharMin(v, getValues('char_limit')),
                          minZero: (v) => noZeros(v),
                        },
                        setValueAs: (v) => (v == '' ? null : v),
                        deps: 'char_limit',
                      })}
                    />
                    <ErrorMessage
                      errors={errors}
                      name='char_min'
                      render={({ messages }) => <Errors messages={messages} />}
                    />
                  </Row>
                  <Row name='Character limits:'>
                    <input
                      type='number'
                      min={0}
                      {...register('char_limit', {
                        validate: {
                          limit: (v) =>
                            checkCharLimit(v, getValues('char_min')),
                          limitZero: (v) => noZeros(v),
                        },
                        setValueAs: (v) => (v == '' ? null : v),
                        deps: 'char_min',
                      })}
                    />
                    <ErrorMessage
                      errors={errors}
                      name='char_limit'
                      render={({ messages }) => <Errors messages={messages} />}
                    />
                  </Row>
                </>
              )}
            </>
          )}
          {f.isPlaceholderType(type) && (
            <Row name='Placeholder:'>
              <input {...register('placeholder')} />
            </Row>
          )}
          {f.isTextType(type) && (
            <Row name='Default Value:'>
              <input {...register('default_value')} />
            </Row>
          )}
          <TimeStamp title='Created' value={created_at} />
          <TimeStamp title='Updated' value={updated_at} />
        </form>
      </div>
    </>
  );
};

export default FieldEdit;
